import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api';
import { formatLocalDateTime } from '../../utils/dateTime';
import { useAuth } from '../../context/AuthContext';
import Pagination from '../Common/Pagination';
import Toast from '../Common/Toast';
import '../Common/CardActions.css';
import ReminderFormModal from './ReminderFormModal';
import './Reminders.css';

const isAdminUser = (user) => {
  return !!user?.is_staff || !!user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin';
};

const formatOffsetMinutes = (minutes) => {
  const val = Number(minutes || 0);
  if (!Number.isFinite(val)) return '—';
  if (val < 60) return `${val} мин`;
  if (val % 1440 === 0) return `${val / 1440} дн.`;
  if (val % 60 === 0) return `${val / 60} ч.`;
  const days = Math.floor(val / 1440);
  const hours = Math.floor((val % 1440) / 60);
  return [days ? `${days} дн.` : null, hours ? `${hours} ч.` : null].filter(Boolean).join(' ');
};

const toLocalDateTime = (value) => {
  const out = formatLocalDateTime(value);
  return out || '—';
};

const RemindersList = () => {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);

  const [reminders, setReminders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [toast, setToast] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitial, setModalInitial] = useState({});
  const [editingReminderId, setEditingReminderId] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  const loadAuctions = useCallback(async () => {
    try {
      const all = [];
      let page = 1;
      while (true) {
        const resp = await apiService.getAuctions(`?page=${page}&page_size=96`);
        const chunk = resp.data?.results || resp.data || [];
        all.push(...chunk);
        if (!resp.data?.next) break;
        page += 1;
        if (page > 200) break;
      }
      setAuctions(all);
    } catch (_) {
      // ignore
    }
  }, []);

  const loadReminders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const all = [];
      let page = 1;
      const allFlag = isAdmin && showAll ? '&all=1' : '';
      while (true) {
        const resp = await apiService.getReminders(`?page=${page}&page_size=96${allFlag}`);
        const chunk = resp.data?.results || resp.data || [];
        all.push(...chunk);
        if (!resp.data?.next) break;
        page += 1;
        if (page > 200) break;
      }
      setReminders(all);
    } catch (err) {
      setError('Ошибка загрузки напоминаний: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, showAll]);

  const loadNotifications = useCallback(async () => {
    try {
      const all = [];
      let page = 1;
      const allFlag = isAdmin && showAll ? '&all=1' : '';
      while (true) {
        const resp = await apiService.listReminderNotifications(`?page=${page}&page_size=96${allFlag}`);
        const chunk = resp.data?.results || resp.data || [];
        all.push(...chunk);
        if (!resp.data?.next) break;
        page += 1;
        if (page > 200) break;
      }
      setNotifications(all);
    } catch (_) {
      // ignore
    }
  }, [isAdmin, showAll]);

  useEffect(() => {
    loadReminders();
    loadNotifications();
    loadAuctions();
  }, [loadReminders, loadNotifications, loadAuctions]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiService.getUiSettings();
        const v = Number(resp?.data?.table_page_size);
        if ([12, 24, 48, 96].includes(v)) {
          setItemsPerPage(v);
        }
      } catch (_) {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage, reminders]);

  const handleItemsPerPageChange = (v) => {
    setItemsPerPage(v);
    setCurrentPage(1);
    apiService.saveUiSettings({ table_page_size: v }).catch(() => {});
  };

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return reminders;
    return (reminders || []).filter((r) => {
      const auction = r.auction_display || {};
      const userInfo = r.user_display || {};
      const hay = `${r.id || ''} ${auction.debtor_name || ''} ${auction.organizer_name || ''} ${r.event_label || ''} ${userInfo.full_name || ''} ${userInfo.email || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [reminders, search]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  const filteredNotifications = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return notifications;
    return (notifications || []).filter((n) => {
      const auction = n.auction_display || {};
      const userInfo = n.user_display || {};
      const hay = `${auction.debtor_name || ''} ${auction.organizer_name || ''} ${n.event_label || ''} ${n.message || ''} ${userInfo.full_name || ''} ${userInfo.email || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [notifications, search]);

  const openCreate = () => {
    setEditingReminderId(null);
    setModalInitial({
      auction_id: '',
      event_type: '',
      offsets_minutes: [],
      channel_email: true,
      channel_push: true,
      existing_reminders: reminders,
    });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingReminderId(item.id);
    setModalInitial({
      auction_id: item.auction,
      event_type: item.event_type,
      offsets_minutes: item.offsets_minutes || [],
      channel_email: item.channel_email,
      channel_push: item.channel_push,
      existing_reminders: reminders,
    });
    setModalOpen(true);
  };

  const handleDelete = async (item) => {
    try {
      await apiService.deleteReminder(item.id);
      showToast('Напоминание удалено');
      loadReminders();
    } catch (err) {
      showToast('Ошибка удаления: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleSubmit = async (payload) => {
    const data = {
      auction: payload.auction_id,
      event_type: payload.event_type,
      offsets_minutes: payload.offsets_minutes || [],
      channel_email: payload.channel_email,
      channel_push: payload.channel_push,
    };
    try {
      if (editingReminderId) {
        await apiService.updateReminder(editingReminderId, data);
        showToast('Напоминание обновлено');
      } else {
        await apiService.createReminder(data);
        showToast('Напоминание создано');
      }
      setModalOpen(false);
      setEditingReminderId(null);
      loadReminders();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      showToast('Ошибка сохранения: ' + msg, 'error');
    }
  };

  const markNotificationRead = async (item) => {
    try {
      await apiService.markReminderNotificationRead(item.id);
      loadNotifications();
    } catch (_) {
      // ignore
    }
  };

  return (
    <div className="reminders-page">
      <div className="reminders-header">
        <h1>Напоминания</h1>
        <div className="reminders-tools">
          <div className="reminders-search">
            <span>🔎</span>
            <input
              type="text"
              placeholder="Поиск по торгам, событию, пользователю"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {isAdmin && (
            <label className="reminders-toggle">
              <input
                type="checkbox"
                checked={showAll}
                onChange={(e) => setShowAll(e.target.checked)}
              />
              <span>Показывать напоминания всех пользователей</span>
            </label>
          )}
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            + Напоминание
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {loading ? (
        <div className="loading">Загрузка...</div>
      ) : (
        <>
          <div className="reminders-table">
            <table>
              <thead>
                <tr>
                  <th>Торги</th>
                  <th>Событие</th>
                  <th>Дата события</th>
                  <th>Напомнить за</th>
                  <th>Каналы</th>
                  <th>Статус</th>
                  <th>Следующее напоминание</th>
                  {isAdmin && <th>Пользователь</th>}
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 9 : 8} style={{ padding: 20, textAlign: 'center', color: '#6c757d' }}>
                      Напоминания не найдены
                    </td>
                  </tr>
                ) : paginated.map((r) => (
                  <tr key={r.id}>
                    <td>
                      Торги №{r.auction_display?.id || r.auction}
                      <div style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                        {r.auction_display?.debtor_name || '—'}
                      </div>
                    </td>
                    <td>{r.event_label || '—'}</td>
                    <td>{toLocalDateTime(r.event_datetime)}</td>
                    <td>
                      {(r.offsets_minutes || []).map(formatOffsetMinutes).join(', ') || '—'}
                    </td>
                    <td>
                      {(r.channel_email ? 'Email' : '')}
                      {r.channel_email && r.channel_push ? ', ' : ''}
                      {(r.channel_push ? 'Push' : '') || '—'}
                    </td>
                    <td>
                      <span className={`reminder-status ${r.status}`}>{r.status === 'active' ? 'Активно' : r.status === 'done' ? 'Выполнено' : 'Отменено'}</span>
                    </td>
                    <td>{toLocalDateTime(r.next_notify_at)}</td>
                    {isAdmin && (
                      <td>
                        {r.user_display?.full_name || '—'}
                        <div style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                          {r.user_display?.email || ''}
                        </div>
                      </td>
                    )}
                    <td>
                      <div className="card-actions" style={{ justifyContent: 'flex-start' }}>
                        <button
                          type="button"
                          className="card-action-btn card-action-btn--edit"
                          title="Редактировать"
                          onClick={() => openEdit(r)}
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className="card-action-btn card-action-btn--delete"
                          title="Удалить"
                          onClick={() => handleDelete(r)}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onItemsPerPageChange={handleItemsPerPageChange}
          />

          <div className="reminder-notifications">
            <h3>Уведомления</h3>
            <div className="reminders-table">
              <table>
                <thead>
                  <tr>
                    <th>Событие</th>
                    <th>Торги</th>
                    <th>Сообщение</th>
                    <th>Создано</th>
                    <th>Статус</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNotifications.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#6c757d' }}>
                        Уведомлений пока нет
                      </td>
                    </tr>
                  ) : filteredNotifications.slice(0, 50).map((n) => (
                    <tr key={n.id}>
                      <td>{n.event_label || '—'}</td>
                      <td>Торги №{n.auction_display?.id || n.auction}</td>
                      <td>{n.message || '—'}</td>
                      <td>{toLocalDateTime(n.created_at)}</td>
                      <td>{n.read_at ? 'Прочитано' : 'Новое'}</td>
                      <td>
                        {!n.read_at && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => markNotificationRead(n)}
                          >
                            Отметить прочитанным
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <ReminderFormModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingReminderId(null);
        }}
        onSubmit={handleSubmit}
        auctions={auctions}
        initialData={modalInitial}
        allowAuctionSelect
        allowEventSelect
      />

      <Toast toast={toast} />
    </div>
  );
};

export default RemindersList;
